import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { UserCircle, Plus, Pencil, Trash2, Loader2, Mail, Phone, Briefcase, Linkedin, X } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AccountManagers() {
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    job_title: '',
    linkedin_url: '',
    profile_picture: ''
  });

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/account-managers`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setManagers(data.managers || []);
      }
    } catch (error) {
      toast.error('Failed to load account managers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (manager = null) => {
    if (manager) {
      setEditingManager(manager);
      setFormData({
        name: manager.name || '',
        email: manager.email || '',
        phone: manager.phone || '',
        job_title: manager.job_title || '',
        linkedin_url: manager.linkedin_url || '',
        profile_picture: manager.profile_picture || ''
      });
    } else {
      setEditingManager(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        job_title: '',
        linkedin_url: '',
        profile_picture: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingManager(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      job_title: '',
      linkedin_url: '',
      profile_picture: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingManager 
        ? `${BACKEND_URL}/api/admin/account-managers/${editingManager.manager_id}`
        : `${BACKEND_URL}/api/admin/account-managers`;
      
      const response = await fetch(url, {
        method: editingManager ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingManager ? 'Account manager updated' : 'Account manager created');
        handleCloseModal();
        fetchManagers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save account manager');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (managerId) => {
    if (!window.confirm('Are you sure you want to delete this account manager? They will be unassigned from all customers.')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/account-managers/${managerId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Account manager deleted');
        fetchManagers();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete account manager');
    }
  };

  // Extract LinkedIn profile picture URL from LinkedIn URL
  const getLinkedInPictureUrl = (linkedinUrl) => {
    if (!linkedinUrl) return null;
    // LinkedIn doesn't allow direct image scraping, return null for now
    // The admin can manually add a profile picture URL
    return null;
  };

  const getProfileImage = (manager) => {
    if (manager.profile_picture) {
      return manager.profile_picture;
    }
    // Generate initials-based placeholder
    const initials = (manager.name || 'AM').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return null; // Will use initials fallback
  };

  if (loading) {
    return (
      <AdminLayout title="Account Managers">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Account Managers">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserCircle className="h-8 w-8 text-secondary" />
            <div>
              <h2 className="text-2xl font-mono font-bold">Account Managers</h2>
              <p className="text-sm text-muted-foreground">
                Manage your team and assign them to customers
              </p>
            </div>
          </div>
          <Button onClick={() => handleOpenModal()} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Account Manager
          </Button>
        </div>

        {/* Managers Grid */}
        {managers.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <UserCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-mono font-bold mb-2">No Account Managers</h3>
            <p className="text-muted-foreground mb-4">Add your first account manager to assign them to customers</p>
            <Button onClick={() => handleOpenModal()} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Account Manager
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {managers.map((manager) => (
              <div key={manager.manager_id} className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    {getProfileImage(manager) ? (
                      <img 
                        src={getProfileImage(manager)} 
                        alt={manager.name}
                        className="h-16 w-16 rounded-full object-cover border-2 border-primary/20"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-xl font-mono font-bold text-primary">
                          {(manager.name || 'AM').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-mono font-bold text-lg">{manager.name}</h3>
                      {manager.job_title && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          {manager.job_title}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenModal(manager)} className="h-8 w-8 p-0">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(manager.manager_id)} className="h-8 w-8 p-0 text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${manager.email}`} className="text-primary hover:underline">{manager.email}</a>
                  </p>
                  {manager.phone && (
                    <p className="text-sm flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <a href={`tel:${manager.phone}`} className="hover:text-primary">{manager.phone}</a>
                    </p>
                  )}
                  {manager.linkedin_url && (
                    <p className="text-sm flex items-center gap-2">
                      <Linkedin className="h-4 w-4 text-muted-foreground" />
                      <a href={manager.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate max-w-[200px]">
                        LinkedIn Profile
                      </a>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="font-mono font-bold text-lg">
                  {editingManager ? 'Edit Account Manager' : 'Add Account Manager'}
                </h3>
                <Button variant="ghost" size="sm" onClick={handleCloseModal} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@xentra.co.uk"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+44 123 456 7890"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="job_title">Job Title</Label>
                  <Input
                    id="job_title"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="Senior Account Manager"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                  <Input
                    id="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                    placeholder="https://linkedin.com/in/johnsmith"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile_picture">Profile Picture URL</Label>
                  <Input
                    id="profile_picture"
                    value={formData.profile_picture}
                    onChange={(e) => setFormData({ ...formData, profile_picture: e.target.value })}
                    placeholder="https://example.com/photo.jpg"
                  />
                  <p className="text-xs text-muted-foreground">
                    Paste a direct URL to the profile picture
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="flex-1">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {editingManager ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AccountManagers;
